import { useGLTF } from "@react-three/drei";
import { useMemo, useEffect } from "react";
import * as THREE from "three";
import GlbLoader from "../../Utils/GlbLoader";
import { assignMaterialToObjects } from "../../Utils/AssignMaterialToObject";
import { stateMemory, stateContentReady } from "../../store";
import { useSnapshot } from "valtio";

export default function HolzarbeitRoomShell({ visible }) {
  const { imperfectionRoughMap } = GlbLoader();
  const snapStateMemory = useSnapshot(stateMemory);
  const contentReady = useSnapshot(stateContentReady)[1];
  const gltf = useGLTF("../models/Museum01/Holzarbeit.glb");

  const mats = useMemo(() => {
    const m = {
      oldWoodMaterial:        new THREE.MeshStandardMaterial({ roughness: 0.8 }),
      orangeGlossyWall:       new THREE.MeshStandardMaterial({ roughness: 0, metalness: 0, color: "#fc8207" }),
      blackGlossyMaterial:    new THREE.MeshStandardMaterial({ roughness: 0.1, color: "#181818", metalness: 0, roughnessMap: imperfectionRoughMap }),
      blackMattMaterial:      new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#181818", roughnessMap: imperfectionRoughMap }),
      blackMattMaterialFloor: new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#262626", roughnessMap: imperfectionRoughMap }),
      blackMattMaterialWall:  new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#262626", roughnessMap: imperfectionRoughMap }),
    };

    assignMaterialToObjects(gltf.scene, ["Dach"], m.blackMattMaterial);
    assignMaterialToObjects(gltf.scene, ["Saeulen", "Walls", "Tennwaende"], m.blackMattMaterialWall);
    assignMaterialToObjects(gltf.scene, ["Podest03", "Podest02", "Podest01", "MiddleGlossy", "Line001", "ewe02", "BlackMatt"], m.blackMattMaterialFloor);
    assignMaterialToObjects(gltf.scene, ["MiddleMatt"], m.blackGlossyMaterial);
    assignMaterialToObjects(gltf.scene, ["Object001"], m.oldWoodMaterial);
    assignMaterialToObjects(gltf.scene, ["ewe03"], m.orangeGlossyWall);

    return m;
  }, [gltf.scene]);

  useEffect(() => {
    if (!visible || !contentReady) return;

    const base = snapStateMemory.memoryLowDevice
      ? "../overallTextures/Holzarbeit/LOW/"
      : "../overallTextures/Holzarbeit/";

    let active = true;
    const loadedTextures = [];

    const track = (tex) => { loadedTextures.push(tex); return tex; };

    const loader = new THREE.TextureLoader();

    loader.load(base + "OldWoodColor.webp", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace;
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      track(tex);
      mats.oldWoodMaterial.map = tex; mats.oldWoodMaterial.needsUpdate = true;
    });
    loader.load(base + "OldWoodNormal.webp", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      track(tex);
      mats.oldWoodMaterial.normalMap = tex; mats.oldWoodMaterial.needsUpdate = true;
    });
    loader.load(base + "OldWoodRough.webp", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      track(tex);
      mats.oldWoodMaterial.roughnessMap = tex; mats.oldWoodMaterial.needsUpdate = true;
    });
    loader.load(base + "BakeHolzarbeitFloorAO.webp", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace; tex.channel = 1;
      track(tex);
      [mats.oldWoodMaterial, mats.blackGlossyMaterial, mats.blackMattMaterialFloor].forEach(m => {
        m.aoMap = tex; m.aoMapIntensity = 0.8; m.needsUpdate = true;
      });
    });
    loader.load(base + "BakeHolzarbeitWallAO.webp", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace; tex.channel = 1;
      track(tex);
      mats.blackMattMaterialWall.aoMap = tex; mats.blackMattMaterialWall.aoMapIntensity = 0.8; mats.blackMattMaterialWall.needsUpdate = true;
    });

    return () => {
      active = false;
      loadedTextures.forEach(tex => tex.dispose());
      mats.oldWoodMaterial.map = null;
      mats.oldWoodMaterial.normalMap = null;
      mats.oldWoodMaterial.roughnessMap = null;
      [mats.oldWoodMaterial, mats.blackGlossyMaterial, mats.blackMattMaterialFloor, mats.blackMattMaterialWall].forEach(m => {
        m.aoMap = null; m.needsUpdate = true;
      });
    };
  }, [visible, contentReady]);

  return <primitive object={gltf.scene} visible={visible} />;
}
